import React, { useState } from 'react'
import { 
  Layout, 
  Row, 
  Col, 
  Card, 
  Input, 
  Select, 
  Button, 
  Typography, 
  Space, 
  Pagination,
  Spin,
  Empty,
  Breadcrumb,
  Tag,
     // Slider removed - not used
} from 'antd'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SearchOutlined, AppstoreOutlined, BarsOutlined, HomeOutlined } from '@ant-design/icons'
import { Helmet } from 'react-helmet-async'
import { useQuery } from '@tanstack/react-query'
import { productsApi, siteSettingsApi, categoriesApi } from '../../services/api'
import NewFlag from '../../components/NewFlag'

const { Content } = Layout
const { Title, Text, Paragraph } = Typography
const { Option } = Select

const ProductsPage: React.FC = () => {
  const { i18n, t } = useTranslation()
  const currentLang = i18n.language as 'en' | 'zh'
  const [searchParams, setSearchParams] = useSearchParams()
  
  // State for filters
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [category, _setCategory] = useState(searchParams.get('category') || '')
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'sortOrder')
  const [order, setOrder] = useState(searchParams.get('order') || 'asc')
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000])
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'))
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  // Fetch site settings for dynamic content
  const { data: siteSettingsData } = useQuery({
    queryKey: ['publicSiteSettings'],
    queryFn: siteSettingsApi.getPublicSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Fetch products with filters
  const { data: productsData, isLoading, error } = useQuery({
    queryKey: ['products', search, category, sortBy, order, currentPage],
    queryFn: () => productsApi.getAll({
      search,
      category,
      sortBy,
      order,
      page: currentPage,
      limit: 12,
      minPrice: priceRange[0],
      maxPrice: priceRange[1]
    })
  })

  // No longer needed - categories come from categoriesApi now

  const settings = siteSettingsData?.settings

  // Helper function to get localized text
  const getLocalizedText = (text: { en: string; zh: string } | undefined, fallback: string = '') => {
    if (!text) return fallback
    return text[currentLang] || text.en || fallback
  }

  // Helper function to filter out generic import-generated content
  const filterGenericContent = (content: string) => {
    if (!content) return ''
    
    // Remove the generic OMRON Healthcare description pattern
    const genericPattern = /Model:\s*[A-Za-z0-9-]+\s*\n\nThis is a high-quality medical device from OMRON Healthcare\. For detailed specifications and features, please refer to the product manual or contact our support team\./g
    
    return content.replace(genericPattern, '').trim()
  }

  // Get categories from the new hierarchical category system
  const { data: categoriesData } = useQuery({
    queryKey: ['publicCategories'],
    queryFn: () => categoriesApi.getAll({ includeEmpty: false }),
    staleTime: 1000 * 60 * 5, // 5 minutes
  })



  // Find the current selected category for dynamic content
  const getCurrentCategory = () => {
    if (!category || !categoriesData?.categories) return null
    
    // Find category by slug in both main categories and subcategories
    for (const cat of categoriesData.categories) {
      if (cat.slug === category) return cat
      
      // Check subcategories if this is a main category
      if (cat.subcategories) {
        const subcat = cat.subcategories.find((sub: any) => sub.slug === category)
        if (subcat) return subcat
      }
    }
    return null
  }

  // Get dynamic page content based on selected category
  const getPageContent = () => {
    const selectedCategory = getCurrentCategory()
    
    if (selectedCategory) {
      return {
        title: getLocalizedText(selectedCategory.name, 'Products'),
        description: getLocalizedText(selectedCategory.description, 'Explore our range of products in this category'),
        categoryName: getLocalizedText(selectedCategory.name, 'Category')
      }
    }
    
    // Default content when no category is selected
    return {
      title: getLocalizedText(settings?.pages?.products?.title, 'Medical Products'),
      description: getLocalizedText(settings?.pages?.products?.description, 'Professional healthcare devices for accurate monitoring and treatment'),
      categoryName: null
    }
  }

  const handleSearch = (value: string) => {
    setSearch(value)
    setCurrentPage(1)
    updateSearchParams({ search: value, page: '1' })
  }



  const handleSortChange = (value: string) => {
    setSortBy(value)
    setCurrentPage(1)
    updateSearchParams({ sortBy: value, page: '1' })
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    updateSearchParams({ page: page.toString() })
  }

  const updateSearchParams = (newParams: Record<string, string>) => {
    const params = new URLSearchParams(searchParams)
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        params.set(key, value)
      } else {
        params.delete(key)
      }
    })
    setSearchParams(params)
  }

  const clearFilters = () => {
    setSearch('')
    setSortBy('sortOrder')
    setOrder('asc')
    setPriceRange([0, 10000])
    setCurrentPage(1)
    // Keep category parameter when clearing filters since it's set by navigation
    const params = new URLSearchParams()
    if (category) {
      params.set('category', category)
    }
    setSearchParams(params)
  }

  const pageContent = getPageContent()

  return (
    <>
      <Helmet>
        <title>{pageContent.title} - {getLocalizedText(settings?.siteName, 'MedCareHub')}</title>
        <meta name="description" content={pageContent.description} />
        <meta name="keywords" content={`products, medical devices, ${getLocalizedText(settings?.seo?.keywords, 'health, medical, monitoring')}`} />
      </Helmet>

      <Content>
        {/* Breadcrumb */}
        <div className="bg-gray-50 py-4">
          <div className="max-w-6xl mx-auto px-4">
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to="/">
                  <HomeOutlined /> {t('navigation.home')}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <Link to="/products">{t('navigation.products')}</Link>
              </Breadcrumb.Item>
              {pageContent.categoryName && (
                <Breadcrumb.Item>{pageContent.categoryName}</Breadcrumb.Item>
              )}
            </Breadcrumb>
          </div>
        </div>

        {/* Banner Section */}
        <section 
          className="h-[40vh] text-white relative bg-center bg-cover flex items-center justify-center"
          style={{
            backgroundImage: `url('${settings?.pages?.products?.bannerImage || '/products-banner.jpg'}')`,
            backgroundColor: '#1890ff' // Fallback color
          }}
        >
          <div className="absolute inset-0 bg-black bg-opacity-40"></div>
          <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
            <Title level={1} className="text-4xl font-bold text-white mb-4 drop-shadow-lg" style={{ color: '#ffffff' }}>
              {pageContent.title}
            </Title>
            <Paragraph className="text-xl text-white max-w-3xl mx-auto drop-shadow-lg">
              {getLocalizedText(settings?.pages?.products?.subtitle) || pageContent.description}
            </Paragraph>
          </div>
        </section>

        {/* Header */}
        <section className="py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-8">
              <Title level={2} className="text-3xl font-bold text-gray-900 mb-4">
                {t('products.filters.title', 'Filter Products')}
              </Title>
            </div>

            {/* Search and Filters */}
            <Row gutter={[16, 16]} className="mb-8" align="middle">
              <Col xs={24} sm={12} lg={10}>
                <Input.Search
                  placeholder={t('products.search.placeholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={handleSearch}
                  size="large"
                  prefix={<SearchOutlined />}
                />
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Select
                  value={sortBy}
                  onChange={handleSortChange}
                  size="large"
                  style={{ width: '100%' }}
                >
                  <Option value="sortOrder">{t('products.filters.sortOptions.displayOrder')}</Option>
                  <Option value="name">{t('products.filters.sortOptions.nameAsc')}</Option>
                  <Option value="createdAt">{t('products.filters.sortOptions.newest')}</Option>
                  <Option value="popularity">{t('products.filters.sortOptions.popular')}</Option>
                </Select>
              </Col>
              <Col xs={12} sm={6} lg={4}>
                <Button 
                  onClick={clearFilters}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {t('common.clearFilters')}
                </Button>
              </Col>
              <Col xs={24} lg={6}>
                <div className="flex justify-center lg:justify-end">
                  <Space>
                    <Button
                      type={viewMode === 'grid' ? 'primary' : 'default'}
                      icon={<AppstoreOutlined />}
                      onClick={() => setViewMode('grid')}
                      size="large"
                    />
                    <Button
                      type={viewMode === 'list' ? 'primary' : 'default'}
                      icon={<BarsOutlined />}
                      onClick={() => setViewMode('list')}
                      size="large"
                    />
                  </Space>
                </div>
              </Col>
            </Row>
          </div>
        </section>

        {/* Products Grid/List */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4">
            {isLoading ? (
              <div className="text-center py-20">
                <Spin size="large" />
                <div className="mt-4 text-gray-500">{t('common.loading')}</div>
              </div>
            ) : error ? (
              <div className="text-center py-20">
                <Empty description={t('messages.error.serverError')} />
              </div>
            ) : !productsData?.products?.length ? (
              <div className="text-center py-20">
                <Empty description={t('products.messages.noProductsFound')} />
              </div>
            ) : (
              <>
                {/* Results count */}
                <div className="mb-6 flex justify-between items-center">
                  <Text className="text-gray-600">
                    {t('products.messages.showingRange', { from: ((currentPage - 1) * 12) + 1, to: Math.min(currentPage * 12, productsData.pagination.total), total: productsData.pagination.total })}
                  </Text>
                </div>

                {/* Products Grid */}
                <Row gutter={[24, 24]} className="mb-8">
                  {productsData.products.map((product: any) => (
                    <Col xs={24} sm={12} lg={viewMode === 'grid' ? 8 : 24} key={product._id}>
                      <Card
                        hoverable
                        cover={
                          <div className={`${viewMode === 'grid' ? 'h-64' : 'h-40'} bg-white flex items-center justify-center relative overflow-hidden p-4`}>
                            {product.images?.[0]?.url ? (
                              <img 
                                src={product.images[0].url} 
                                alt={product.images[0].alt?.en || product.name.en}
                                className="w-full h-full object-contain rounded"
                              />
                            ) : (
                              <span className="text-gray-400">{t('common.noImage')}</span>
                            )}
                            {product.isNew && <NewFlag />}
                          </div>
                        }
                        className="h-full"
                      >
                        <div className="mb-4">
                          <Title level={4} className="mb-2 line-clamp-2">
                            {getLocalizedText(product.name, 'Product')}
                          </Title>
                          <div 
                            className="text-gray-600 line-clamp-3"
                            dangerouslySetInnerHTML={{ 
                              __html: filterGenericContent(getLocalizedText(product.description, 'Product description')) 
                            }}
                          />
                        </div>
                        <div className="mt-4">
                          {product.category && (
                            <Tag color="blue" className="mb-3">
                              {typeof product.category === 'object' && product.category.name 
                                ? getLocalizedText(product.category.name, t('products.filters.category'))
                                : getLocalizedText(product.category, t('products.filters.category'))
                              }
                            </Tag>
                          )}
                          {product.tags && product.tags.length > 0 && (
                            <div className="mb-3">
                              {product.tags.map((tag: any, index: number) => (
                                <Tag key={index} color="green" className="mb-1">
                                  #{getLocalizedText(tag, 'Tag')}
                                </Tag>
                              ))}
                            </div>
                          )}
                          <div className="text-center">
                            <Button type="primary" ghost>
                              <Link to={`/products/${product._id}`}>
                                {t('common.viewMore')}
                              </Link>
                            </Button>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>

                {/* Pagination */}
                <div className="text-center">
                  <Pagination
                    current={currentPage}
                    total={productsData.pagination.total}
                    pageSize={12}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total, range) => t('products.messages.paginationTotal', { from: range[0], to: range[1], total })}
                  />
                </div>
              </>
            )}
          </div>
        </section>
      </Content>
    </>
  )
}

export default ProductsPage
